import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@finance/components/Layout";
import { api, fmtNum, errMsg } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { DateField } from "@finance/components/DateField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, CaretLeft, CaretRight, CheckCircle, PencilSimple, CalendarBlank, MagicWand } from "@phosphor-icons/react";

const CATEGORIES = ["VAT", "PAYE / SDL", "NSSF / WCF", "WHT", "Corporate Tax", "Provisional Tax", "Licence Renewal", "Statutory Filing", "Loan Repayment", "Other"];
const RECURRENCE = ["None", "Monthly", "Quarterly", "Annual"];
const d10 = (s) => (s || "").slice(0, 10);
const CAT_COLOR = (c) => ({
  "VAT": "bg-blue-100 text-blue-700", "PAYE / SDL": "bg-purple-100 text-purple-700",
  "NSSF / WCF": "bg-teal-100 text-teal-700", "WHT": "bg-amber-100 text-amber-700",
  "Corporate Tax": "bg-rose-100 text-rose-700", "Provisional Tax": "bg-rose-50 text-rose-600",
  "Licence Renewal": "bg-emerald-100 text-emerald-700", "Statutory Filing": "bg-indigo-100 text-indigo-700",
  "Loan Repayment": "bg-orange-100 text-orange-700",
}[c] || "bg-gray-100 text-gray-600");

const emptyForm = { title: "", category: "VAT", due_date: "", amount: "", currency: "TZS", recurrence: "None", reminder_days: 3, notes: "" };

export default function FinanceCalendar() {
  const [events, setEvents] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState(null); // null = closed; object = open (add/edit)
  const [showDone, setShowDone] = useState(false);

  const load = () => api.get("/finance-calendar").then((r) => setEvents(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Enter a title"); return; }
    if (!form.due_date) { toast.error("Pick a due date"); return; }
    try {
      if (form.id) await api.patch(`/finance-calendar/${form.id}`, form);
      else await api.post("/finance-calendar", form);
      toast.success(form.id ? "Deadline updated" : "Deadline added");
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const markDone = async (e) => {
    try { const r = await api.post(`/finance-calendar/${e.id}/done`); toast.success(r.data.next ? "Done — next occurrence scheduled" : "Marked done"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (e) => { if (!window.confirm(`Delete "${e.title}"?`)) return; await api.delete(`/finance-calendar/${e.id}`); load(); };
  const seedStatutory = async () => {
    try {
      const r = await api.post(`/finance-calendar/seed-statutory?month=${calMonth}`);
      if (r.data.created > 0) toast.success(`Added ${r.data.created} statutory deadline(s) for ${calMonth}`);
      else toast.info("Statutory dates are already in for this month");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [cy, cm] = calMonth.split("-").map(Number);
  const firstDow = new Date(cy, cm - 1, 1).getDay();
  const daysInMonth = new Date(cy, cm, 0).getDate();
  const shiftMonth = (delta) => { const dt = new Date(cy, cm - 1 + delta, 1); setCalMonth(dt.toISOString().slice(0, 7)); };
  const dstrOf = (dd) => `${cy}-${String(cm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  const openEvents = events.filter((e) => e.status !== "Done");
  const eventsOnDay = (dd) => openEvents.filter((e) => d10(e.due_date) === dstrOf(dd));
  const cells = []; for (let i = 0; i < firstDow; i++) cells.push(null); for (let dd = 1; dd <= daysInMonth; dd++) cells.push(dd);
  const upcoming = [...openEvents].sort((a, b) => d10(a.due_date).localeCompare(d10(b.due_date)));
  const doneList = events.filter((e) => e.status === "Done");
  const dlColor = (n) => n == null ? "text-gray-400" : n < 0 ? "text-red-600" : n <= 3 ? "text-[#1B4383]" : n <= 7 ? "text-amber-600" : "text-gray-500";

  return (
    <Layout>
      <PageHeader title="Finance Calendar" subtitle="Shared statutory payables & deadlines — reminders go to Finance a few days before each due date" />
      <div className="p-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">A shared register the whole finance team can maintain. Standard TRA/NSSF filing dates are auto-added each month; recurring deadlines roll forward when marked done.</div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="fc-seed-btn" onClick={seedStatutory}><MagicWand size={16} className="mr-1" />Auto-fill statutory dates</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="fc-add-btn" onClick={() => setForm({ ...emptyForm })}><Plus size={16} className="mr-1" />Add Deadline</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Calendar grid */}
          <div className="lg:col-span-2 rounded-sm border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <button className="rounded-sm border border-gray-200 p-1.5 hover:bg-gray-50" data-testid="fc-cal-prev" onClick={() => shiftMonth(-1)}><CaretLeft size={16} /></button>
              <div className="font-display text-base font-semibold" data-testid="fc-cal-title">{new Date(cy, cm - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
              <button className="rounded-sm border border-gray-200 p-1.5 hover:bg-gray-50" data-testid="fc-cal-next" onClick={() => shiftMonth(1)}><CaretRight size={16} /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center lx-label">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((dd, i) => {
                if (!dd) return <div key={`e${i}`} />;
                const on = eventsOnDay(dd);
                const isToday = dstrOf(dd) === new Date().toISOString().slice(0, 10);
                return (
                  <div key={dd} data-testid={`fc-cal-day-${dd}`} className={`min-h-[68px] rounded-sm border p-1 text-left ${on.length ? "border-[#1B4383]/40 bg-[#E6ECF5]/40" : "border-gray-100"}`}>
                    <div className={`text-[11px] font-medium ${isToday ? "text-[#1B4383] font-bold" : "text-gray-500"}`}>{dd}</div>
                    {on.slice(0, 3).map((e) => (
                      <button key={e.id} onClick={() => setForm({ ...emptyForm, ...e, amount: e.amount ?? "" })} title={e.title}
                        className={`mt-0.5 block w-full truncate rounded-sm px-1 text-left text-[10px] ${CAT_COLOR(e.category)}`}>{e.title}</button>
                    ))}
                    {on.length > 3 && <div className="mt-0.5 text-[9px] text-gray-400">+{on.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming list */}
          <div className="space-y-4">
            <div className="rounded-sm border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-gray-700"><CalendarBlank size={16} />Upcoming Deadlines</div>
              {upcoming.length === 0 && <div className="py-6 text-center text-xs text-gray-400">No open deadlines. Add one to get reminders.</div>}
              <div className="space-y-1.5" data-testid="fc-upcoming-list">
                {upcoming.map((e) => (
                  <div key={e.id} data-testid={`fc-event-${e.id}`} className="rounded-sm border border-gray-100 p-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-800">{e.title}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
                          <span className={`rounded-sm px-1.5 py-0.5 ${CAT_COLOR(e.category)}`}>{e.category}</span>
                          {e.auto_seed && <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-gray-400" title="Auto-added statutory deadline">Auto</span>}
                          <span className="text-gray-500 lx-num">{d10(e.due_date)}</span>
                          {e.recurrence && e.recurrence !== "None" && <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-gray-500">↻ {e.recurrence}</span>}
                        </div>
                        {e.amount != null && <div className="mt-0.5 text-xs text-gray-500 lx-num">{e.currency} {fmtNum(e.amount)}</div>}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className={`text-xs font-semibold ${dlColor(e.days_left)}`}>{e.days_left == null ? "—" : e.days_left < 0 ? `${Math.abs(e.days_left)}d overdue` : e.days_left === 0 ? "due today" : `${e.days_left}d`}</div>
                        <div className="mt-1 flex justify-end gap-1.5">
                          <button className="text-emerald-600 hover:text-emerald-700" title="Mark done" data-testid={`fc-done-${e.id}`} onClick={() => markDone(e)}><CheckCircle size={16} /></button>
                          <button className="text-blue-500 hover:text-blue-700" title="Edit" data-testid={`fc-edit-${e.id}`} onClick={() => setForm({ ...emptyForm, ...e, amount: e.amount ?? "" })}><PencilSimple size={15} /></button>
                          <button className="text-gray-300 hover:text-red-500" title="Delete" data-testid={`fc-del-${e.id}`} onClick={() => del(e)}><Trash size={15} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-sm border border-gray-200 bg-white p-4">
              <button className="flex w-full items-center justify-between font-display text-sm font-semibold text-gray-700" data-testid="fc-toggle-done" onClick={() => setShowDone((v) => !v)}>
                <span>Completed ({doneList.length})</span><CaretRight size={14} className={`transition-transform ${showDone ? "rotate-90" : ""}`} />
              </button>
              {showDone && (
                <div className="mt-2 space-y-1" data-testid="fc-done-list">
                  {doneList.length === 0 && <div className="py-3 text-center text-xs text-gray-400">Nothing completed yet.</div>}
                  {doneList.map((e) => (
                    <div key={e.id} className="flex items-center justify-between rounded-sm border border-gray-100 px-2 py-1 text-xs">
                      <span className="truncate text-gray-500 line-through">{e.title} · {d10(e.due_date)}</span>
                      <button className="text-gray-300 hover:text-red-500" data-testid={`fc-del-done-${e.id}`} onClick={() => del(e)}><Trash size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent data-testid="fc-dialog">
          <DialogHeader><DialogTitle className="font-display">{form?.id ? "Edit Deadline" : "Add Deadline"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="lx-label">Title</Label><Input className="mt-1" data-testid="fc-f-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. VAT Return — September" /></div>
              <div><Label className="lx-label">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="fc-f-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="lx-label">Due Date</Label><DateField className="mt-1" data-testid="fc-f-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label className="lx-label">Amount (optional)</Label><Input type="number" className="mt-1" data-testid="fc-f-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><Label className="lx-label">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="mt-1" data-testid="fc-f-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{["TZS", "USD", "EUR", "GBP", "KES"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="lx-label">Repeats</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger className="mt-1" data-testid="fc-f-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>{RECURRENCE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="lx-label">Remind (days before)</Label><Input type="number" className="mt-1" data-testid="fc-f-reminder" value={form.reminder_days} onChange={(e) => setForm({ ...form, reminder_days: e.target.value })} /></div>
              <div className="col-span-2"><Label className="lx-label">Notes</Label><Input className="mt-1" data-testid="fc-f-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="fc-save" onClick={save}>{form?.id ? "Update" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
